/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { first, map } from "rxjs/operators";
import { AbstractObjectList } from "@src/app/model/abstract-object";
import { User } from "@src/app/model/user";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase } from "@src/app/modules/importer/General/ParseOptionBase";
import { ParseOptionBaseMapped } from "@src/app/modules/importer/General/ParseOptionBaseMapped";
import { POBValidators, requiredNumber } from "@src/app/modules/importer/General/POBValidators";
import { BCodeService } from "@src/app/modules/budget/bcode.service";
import { BCode } from "@src/app/model/bcode";
import { CycleService } from "@src/app/modules/budget/cycle.service";
import { ImportRow } from "@src/app/modules/importer/ImportRow";

@Injectable({
    providedIn: 'root'
})
export class BudgetCodeParserService implements ImportParser {

    gps = new GeneralParser();
    bCodeSvc = inject(BCodeService);
    cycleSvc = inject(CycleService);
    currentUserSvc = inject(CurrentUserService);

    bCodes : BCode[] = [];

    importDoc: ImportDoc;
    currentUser: User;

    namePob = new ParseOptionBase('Name', 'name', [POBValidators.requiredPOB()]);
    sortPob = new ParseOptionBase('Sort', 'sort', [requiredNumber]);
    typePob = new ParseOptionBaseMapped('Type', 'typeId');
    notesPob = new ParseOptionBase('Notes', 'notes');
    systemPob = new ParseOptionBaseMapped('System Account', 'systemAccountId').allowNull();
    bankAccountPob = new ParseOptionBaseMapped('Bank Account', 'bankAccountId').allowNull();
    integrationIdPob = new ParseOptionBase('IntegrationId', 'integrationId');

    availableImportItems = [
        this.namePob,
        this.sortPob,
        this.typePob,
        this.notesPob,
        this.systemPob,
        this.integrationIdPob
    ];

    constructor() {
        this.cycleSvc.get(); // Will need these, they should be cached by now, surely...
    }
/* 
Enforcing Unique Name/Sort will make it fail if the incoming name / sort happens to match the existing for a system account, 
for now, let it through - DB will reject Duplicates, and we'll need docs to explain.

    uniqueNameValidator() {
        const pobValidator = new ParseValidator();

        pobValidator.validateRow = (field: ImportField) => {
            const val = field.getValue();
            const val2 = (val ? val.toString() : '');
            const bCode = this.bCodes.find( u => u.name.trim() === val2.trim());
            if (bCode) {
                return $localize` Budget Code Name exists already`;
            }
            return null;
        }

        return pobValidator;

    }

    uniqueSortValidator() {
        const pobValidator = new ParseValidator();

        pobValidator.validateRow = (field: ImportField) => {
            const val = field.getValue();
            const bCode = this.bCodes.find(u => u.sort == val);
            if (bCode) {
                return $localize` Budget Code Name exists already`;
            }
            return null;
        }

        return pobValidator;
    }
*/

    setUp() {
        this.importDoc = new ImportDoc();
        console.log('Setting up');
        /** without .pipe(first()) getDefaultBCode ne'er returns */
        return forkJoin({
            bCodes: this.bCodeSvc.get<BCode>(true),
            currentUser: this.currentUserSvc.getCurrentUser().pipe(first()),
            defaultBCodes: this.currentUserSvc.getDefaultBCodes().pipe(first())
        }).pipe(map(result => {
            this.bCodes = result.bCodes;
            this.typePob.setOptions(BCode.TYPES, this.importDoc);
            const defBCodes = result.defaultBCodes.map((dbc) => ({
                name: dbc.type,
                id: dbc.bCodeId,
            }));
            this.systemPob.setOptions(defBCodes, this.importDoc);
            return true;
        }));
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    validateRow(line: ImportRow, data: BCode) {
        if (!data.typeId) {
            line.addError('TypeId needs a value');
        }
        if (!data.name) {
            line.addError('Name needs a value');
        }
        if (!data.sort) {
            line.addError('Sort needs a value');
        }
    }

    postToServer(): Observable<AbstractObjectList> {
        const postItems = this.gps.getItemsToPost({validateItemFn: this.validateRow.bind(this)});
        console.log(postItems);

        if (!this.importDoc.hasErrors()) {
            return this.bCodeSvc.postItems(postItems);
        }
        return of(null);

    }
}
